import {
  HEADER_QUICK_NAV_MAX,
  getPriorityRank,
} from '../constants/navigationItems';
import { scopedKey, getActiveAccountId } from './accountSession';

export const NAV_CLICK_COUNTS_KEY = 'nav_click_counts';
export const NAV_USAGE_UPDATED_EVENT = 'nav-usage-updated';

export function readNavClickCounts() {
  try {
    const raw = localStorage.getItem(scopedKey(NAV_CLICK_COUNTS_KEY, getActiveAccountId()));
    const parsed = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

export function writeNavClickCounts(counts) {
  localStorage.setItem(scopedKey(NAV_CLICK_COUNTS_KEY, getActiveAccountId()), JSON.stringify(counts));
  window.dispatchEvent(
    new CustomEvent(NAV_USAGE_UPDATED_EVENT, { detail: { counts } }),
  );
}

export function recordNavClick(itemId) {
  if (!itemId) return readNavClickCounts();
  const counts = readNavClickCounts();
  counts[itemId] = (counts[itemId] || 0) + 1;
  writeNavClickCounts(counts);
  return counts;
}

export function getNavClickCount(counts, itemId) {
  return counts?.[itemId] || 0;
}

function compareByUsageThenPriority(a, b, counts) {
  const aClicks = getNavClickCount(counts, a.id);
  const bClicks = getNavClickCount(counts, b.id);
  if (bClicks !== aClicks) return bClicks - aClicks;
  const aRank = getPriorityRank(a.path);
  const bRank = getPriorityRank(b.path);
  if (aRank !== bRank) return aRank - bRank;
  return String(a.label || '').localeCompare(String(b.label || ''), undefined, {
    sensitivity: 'base',
  });
}

/** Items the user has opened at least once, sorted by click count. */
export function getFrequentNavItems(items, counts) {
  return items
    .filter((item) => getNavClickCount(counts, item.id) > 0)
    .sort((a, b) => compareByUsageThenPriority(a, b, counts));
}

/** Items never opened; sorted by default priority for discoverability. */
export function getUnusedNavItems(items, counts) {
  return items
    .filter((item) => getNavClickCount(counts, item.id) === 0)
    .sort((a, b) => {
      const aRank = getPriorityRank(a.path);
      const bRank = getPriorityRank(b.path);
      if (aRank !== bRank) return aRank - bRank;
      return String(a.label || '').localeCompare(String(b.label || ''), undefined, {
        sensitivity: 'base',
      });
    });
}

/** Top N destinations by usage (clicks, then default priority). */
export function getHeaderSlotMembers(items, counts, max = HEADER_QUICK_NAV_MAX) {
  return [...items]
    .sort((a, b) => compareByUsageThenPriority(a, b, counts))
    .slice(0, max);
}

/**
 * Header slots in fixed priority order; size tier 1 = largest … 8 = smallest by clicks.
 */
export function buildHeaderQuickSlots(members, counts) {
  const positionOrdered = [...members].sort((a, b) => {
    const rankDiff = getPriorityRank(a.path) - getPriorityRank(b.path);
    if (rankDiff !== 0) return rankDiff;
    return String(a.label || '').localeCompare(String(b.label || ''), undefined, {
      sensitivity: 'base',
    });
  });

  const clickValues = positionOrdered.map((item) => getNavClickCount(counts, item.id));
  const maxClicks = Math.max(...clickValues, 0);
  const minClicks = Math.min(...clickValues, 0);

  return positionOrdered.map((item) => {
    const clicks = getNavClickCount(counts, item.id);
    return {
      item,
      clicks,
      sizeTier: getHeaderSizeTier(clicks, minClicks, maxClicks),
    };
  });
}

/** 1 = largest box, 8 = smallest. */
export function getHeaderSizeTier(clicks, minClicks, maxClicks) {
  if (maxClicks === minClicks) return 5;
  const ratio = (clicks - minClicks) / (maxClicks - minClicks);
  return Math.max(1, Math.min(8, Math.round(8 - ratio * 7)));
}

/** Items beyond the header top 8, sorted by usage. */
export function getOverflowNavItems(items, counts, headerMemberIds, max = HEADER_QUICK_NAV_MAX) {
  const headerIds =
    headerMemberIds instanceof Set ? headerMemberIds : new Set(headerMemberIds);
  return items
    .filter((item) => !headerIds.has(item.id))
    .sort((a, b) => compareByUsageThenPriority(a, b, counts));
}

export function partitionNavItems(items, counts) {
  const headerSlotMembers = getHeaderSlotMembers(items, counts);
  const headerMemberIds = new Set(headerSlotMembers.map((item) => item.id));
  const headerQuickSlots = buildHeaderQuickSlots(headerSlotMembers, counts);
  const moreItems = getOverflowNavItems(items, counts, headerMemberIds);

  return {
    frequentItems: getFrequentNavItems(items, counts),
    unusedItems: getUnusedNavItems(items, counts),
    headerSlotMembers,
    headerQuickSlots,
    headerQuickItems: headerSlotMembers,
    moreItems,
  };
}
